import i18n from 'i18next'
import { InitOptions } from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import Backend from 'i18next-http-backend'

import { SUPPORTED_LANGUAGES, type SupportedLanguage, detectLocale } from '@/lib/i18n'

// Helper function to detect initial language
function detectInitialLanguage(): SupportedLanguage {
  // First check URL query param
  const urlParams = new URLSearchParams(window.location.search)
  const langParam = urlParams.get('lang')
  if (SUPPORTED_LANGUAGES.includes(langParam as SupportedLanguage)) return langParam as SupportedLanguage

  // Then check navigator language
  return detectLocale(navigator.language)
}

const initialLanguage = detectInitialLanguage()

const i18nConfig: InitOptions = {
  debug: import.meta.env.DEV,
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LANGUAGES,
  load: 'currentOnly',
  defaultNS: 'common',
  fallbackNS: 'common',
  ns: ['common', 'quiz-ui'],
  lng: initialLanguage,
  interpolation: {
    escapeValue: false,
  },
  detection: {
    order: ['navigator', 'querystring'], // Navigator first
    caches: [],
    lookupQuerystring: 'lang',
  },
  backend: {
    loadPath: '/locales/{{lng}}/{{ns}}.json',
  },
  // Ensure synchronous initialization
  initImmediate: false
}

// Initialize i18next synchronously
i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(i18nConfig)

// Add debug listener
if (import.meta.env.DEV) {
  i18n.on('languageChanged', (lng) => {
    console.log('Language changed to:', lng);
    console.log('Navigator language:', navigator.language);
  });

  // Add HMR listener for translation updates
  if (import.meta.hot) {
    import.meta.hot.on('translations-update', () => {
      i18n.reloadResources().then(() => {
        i18n.changeLanguage(i18n.language)
      })
    })
  }
}

export default i18n

// Basic type declaration for i18next
declare module 'i18next' {
  interface CustomTypeOptions {
    resources: {
      [namespace: string]: {
        [key: string]: any
      }
    }
  }
}